import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import { useMessagerieStore } from "@store/messagerieStore";
import { isGUEST } from "@helpers/accessHelpers";
import { communicationsApi } from "@services/api/organizations";
import ComponentsScreen from "@screens/ComponentsScreen";
import LoginScreen from "@screens/auth/LoginScreen";
import AcceptInvitationScreen from "@screens/auth/AcceptInvitationScreen";
import SideBar from "./SideBar";
import { ErrorProvider } from "context/ErrorContext";
import { NavigationProvider } from "context/NavigationContext";
import { NotificationProvider } from "context/NotificationContext";
import SettingsScreen from "@screens/SettingsScreen";
import ProjectsScreen from "@screens/ProjectsScreen";
import ProjectScreen from "@screens/ProjectScreen";
import PartnersScreen from "@screens/PartnersScreen";
import BroadcastsScreen from "@screens/BroadcastsScreen";
import EmailsSentScreen from "@screens/EmailsSentScreen";
import HomeScreen from "@screens/HomeScreen";
import ReceptionScreen from "@screens/reception/ReceptionScreen";
import AtelierScreen from "@screens/atelier/AtelierScreen";
import BookingDebugScreen from "@screens/BookingDebugScreen";
import LoadTestScreen from "@screens/LoadTestScreen";
import SignScreen from "@screens/SignScreen";
import TrackingScreen from "@screens/TrackingScreen";
import TrackingScreenV2 from "@screens/TrackingScreenV2";
import DevisScreen from "@screens/DevisScreen";
import WatermarkScreen from "@screens/WatermarkScreen";
import VehiculesScreen from "@screens/VehiculesScreen";
import MessagerieScreen from "@screens/MessagerieScreen";
import DossiersScreen from "@screens/DossiersScreen";
import DossierDetailScreen from "@screens/DossierDetailScreen";
import OrdresScreen from "@screens/OrdresScreen";
import OrdreDetailScreen from "@screens/OrdreDetailScreen";
import CatalogScreen from "@screens/CatalogScreen";
import MobileLoginScreen from "@screens/mobile/MobileLoginScreen";
import MobileOrdresScreen from "@screens/mobile/MobileOrdresScreen";
import MobileOrdreDetailScreen from "@screens/mobile/MobileOrdreDetailScreen";
import MobilePlanningScreen from "@screens/mobile/MobilePlanningScreen";
import PrivacyPolicyScreen from "@screens/PrivacyPolicyScreen";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);
  return isMobile;
}

/** Redirects small screens to /m unless already on a mobile route */
function MobileRedirect() {
  const isMobile = useIsMobile();
  const location = useLocation();
  if (isMobile && !location.pathname.startsWith("/m")) {
    return <Navigate to="/m" replace />;
  }
  return null;
}

/** Redirects large screens back from /m to desktop view */
function DesktopRedirect() {
  const isMobile = useIsMobile();
  const location = useLocation();
  if (!isMobile && location.pathname.startsWith("/m")) {
    return <Navigate to="/" replace />;
  }
  return null;
}

/** On small screens, redirect /login → /m (mobile login) */
function MobileLoginRedirect() {
  const isMobile = useIsMobile();
  if (isMobile) return <Navigate to="/m" replace />;
  return <LoginScreen />;
}

function FloatingNewRDVButton() {
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);
  const isGuest = isGUEST(userData);

  if (isGuest || !userData || !orgData?.id) return null;

  const bookingUrl = `https://booking.heric.hexaflow.fr?org_id=${orgData.id}&mode=page&v=3`;

  return (
    <a
      href={bookingUrl}
      target="_blank"
      rel="noopener noreferrer"
      title="Nouveau RDV"
      className="fixed right-20 z-50 w-12 h-12 rounded-full bg-neutral-900 text-white flex items-center justify-center shadow-lg hover:scale-105 transition-transform cursor-pointer"
      style={{ bottom: 20 }}
    >
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
        <path d="M11 4v14M4 11h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </a>
  );
}

function MainLayout() {
  const initGlobal = useMessagerieStore((s) => s.initGlobal);
  const orgMembers = useDataStore((s) => s.orgMembers);
  const [lowBalance, setLowBalance] = useState<number | null>(null);
  const [balanceDismissed, setBalanceDismissed] = useState(false);

  useEffect(() => {
    initGlobal();
  }, [orgMembers]);

  useEffect(() => {
    communicationsApi.getSpending().then((s) => {
      if (s.solde < 6) setLowBalance(s.solde);
    }).catch(() => {});
  }, []);

  return (
    <div className="flex flex-row overflow-x-hidden">
      <MobileRedirect />
      <SideBar />
      <FloatingNewRDVButton />
      <div className="flex-1 min-w-0 relative flex flex-col">
        {lowBalance !== null && !balanceDismissed && (
          <div className="flex flex-row items-center gap-3 px-4 py-3" style={{ background: "#FEF3C7", borderBottom: "1px solid #F59E0B" }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2L1 18h18L10 2z" fill="#F59E0B" stroke="#D97706" strokeWidth="1"/>
              <text x="10" y="15" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#92400E">!</text>
            </svg>
            <span className="text-sm font-medium flex-1" style={{ color: "#92400E" }}>
              Solde insuffisant ({lowBalance.toFixed(2)} EUR) — Les messages WhatsApp, emails, signatures et extractions IA pourraient être bloqués. Rechargez votre solde dans Paramètres {">"} Facturation.
            </span>
            <button onClick={() => setBalanceDismissed(true)} className="flex-shrink-0 p-1 rounded hover:bg-amber-200 transition-colors">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1l12 12M13 1L1 13" stroke="#92400E" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        )}
        <Outlet />
      </div>
    </div>
  );
}

export default function Navigation() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const userData = useDataStore((s) => s.userData);
  const isGuest = isGUEST(userData);

  if (loading) {
    return <div className="bg-neutral-50 h-screen w-screen" />; // Loading state
  }

  return (
    <div className="bg-neutral-50">
      <ErrorProvider>
        <NotificationProvider>
          <BrowserRouter>
            <NavigationProvider>
              <Routes>
                {/* Public routes — accessible without authentication */}
                <Route path="/privacy" element={<PrivacyPolicyScreen />} />
                <Route path="/sign/:token" element={<SignScreen />} />
                <Route path="/devis/:token" element={<DevisScreen />} />
                <Route path="/tracking/:id" element={<TrackingScreenV2 />} />
                <Route path="/tracking-v1/:id" element={<TrackingScreen />} />
                <Route path="/watermark" element={<WatermarkScreen />} />
                <Route path="/accept-invitation" element={<AcceptInvitationScreen />} />
                {user ? (
                  <>
                    {/* Mobile routes — no sidebar */}
                    <Route path="/m" element={<><DesktopRedirect /><MobileOrdresScreen /></>} />
                    <Route path="/m/planning" element={<><DesktopRedirect /><MobilePlanningScreen /></>} />
                    <Route path="/m/ordre/:id" element={<><DesktopRedirect /><MobileOrdreDetailScreen /></>} />

                    <Route element={<MainLayout />}>
                      <Route
                        path="/"
                        element={<Navigate to="/home" replace />}
                      />
                      <Route path="/home" element={<HomeScreen />} />
                      <Route path="/reception" element={<ReceptionScreen />} />
                      <Route path="/atelier" element={<AtelierScreen />} />
                      <Route path="/dossiers" element={<DossiersScreen />} />
                      <Route path="/dossier/:id" element={<DossierDetailScreen />} />
                      <Route path="/ordres" element={<OrdresScreen />} />
                      <Route path="/ordre/:id" element={<OrdreDetailScreen />} />
                      <Route path="/vehicules" element={<VehiculesScreen />} />
                      <Route path="/catalog" element={<CatalogScreen />} />
                      <Route path="/messagerie" element={<MessagerieScreen />} />
                      {/* Legacy Hexaflow routes — kept for template reuse
                      <Route path="/projects" element={<ProjectsScreen />} />
                      <Route path="/project/:id" element={<ProjectScreen />} />
                      <Route
                        path="/partners"
                        element={isGuest ? <Navigate to="/projects" replace /> : <PartnersScreen />}
                      />
                      <Route
                        path="/broadcasts"
                        element={isGuest ? <Navigate to="/projects" replace /> : <BroadcastsScreen />}
                      />
                      <Route
                        path="/emails-sent"
                        element={isGuest ? <Navigate to="/projects" replace /> : <EmailsSentScreen />}
                      />
                      */}
                      <Route path="/booking-debug" element={<BookingDebugScreen />} />
                      <Route path="/load-test" element={<LoadTestScreen />} />
                      <Route path="/settings" element={<SettingsScreen />} />
                      <Route
                        path="/components"
                        element={isGuest ? <Navigate to="/projects" replace /> : <ComponentsScreen />}
                      />
                      <Route
                        path="*"
                        element={<Navigate to="/home" replace />}
                      />
                    </Route>
                  </>
                ) : (
                  <>
                    <Route path="/m" element={<MobileLoginScreen />} />
                    <Route path="/m/*" element={<MobileLoginScreen />} />
                    <Route path="/login" element={<MobileLoginRedirect />} />
                    <Route
                      path="*"
                      element={<Navigate to="/login" replace />}
                    />
                  </>
                )}
              </Routes>
            </NavigationProvider>
          </BrowserRouter>
        </NotificationProvider>
      </ErrorProvider>
    </div>
  );
}
